/* eslint-disable react/display-name */
import React from 'react';
import { SERVER_URL } from '../../config';
import { DARK_COLOR_FACTOR, FORM_DATA, LIGHT_COLOR_FACTOR } from './constants';
import {
	mobileCheck, pSBC, getLanguageItems as getTranslateItems, getText
} from './functions';
import { ReactComponent as ArrowDownIcon } from '../assets/svg/arrowDown.svg';
import { ReactComponent as ArrowLeftMobileIcon } from '../assets/svg/arrowLeftMobile.svg';
import { ReactComponent as BinIcon } from '../assets/svg/bin.svg';
import { ReactComponent as BurguerMenuIcon } from '../assets/svg/burguerMenu.svg';
import { ReactComponent as BoxSelectedIcon } from '../assets/svg/boxSelected.svg';
import { ReactComponent as BoxIcon } from '../assets/svg/box.svg';
import { ReactComponent as CheckCircleIcon } from '../assets/svg/checkCircle.svg';
import { ReactComponent as CrossCircleIcon } from '../assets/svg/crossCircle.svg';
import { ReactComponent as DocumentIcon } from '../assets/svg/document.svg';
import { ReactComponent as MoreCircleIcon } from '../assets/svg/moreCircle.svg';
import { ReactComponent as SearchIcon } from '../assets/svg/search.svg';
import { ReactComponent as WarningCircleIcon } from '../assets/svg/warningCircle.svg';
import { ReactComponent as AlertIcon } from '../assets/svg/alert.svg';
import { ReactComponent as SpinnerIcon } from '../assets/svg/spinner.svg';
import { ReactComponent as TriangleIcon } from '../assets/svg/triangle.svg';
import { ReactComponent as CircleIcon } from '../assets/svg/circle.svg';
import { ReactComponent as CircleCheckedIcon } from '../assets/svg/circleChecked.svg';
import { ReactComponent as Cross } from '../assets/svg/cross.svg';

import { moment } from '../../containers/App';

/* https://www.reddit.com/r/reactjs/comments/9zniy8/import_svg_and_edit_fill_color/eaal5qj?utm_source=share&utm_medium=web2x&context=3 */
export function useIcons(colorObject) {
	const [colors, setColors] = React.useState(colorObject);

	React.useEffect(() => {
		setColors(colorObject);
	}, [colorObject]);

	const Icons = React.useMemo(() => ({
		ArrowDown: ({ className, style }) => <ArrowDownIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		ArrowLeftMobile: ({ className, style }) => <ArrowLeftMobileIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		Bin: ({ className, style }) => <BinIcon style={{ fill: colors?.error, ...style }} className={className} />,
		BurguerMenu: ({ className, style }) => <BurguerMenuIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		Box: ({ className, style }) => <BoxIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		BoxSelected: ({ className, style }) => <BoxSelectedIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		CheckCircle: ({ className, style }) => <CheckCircleIcon style={{ fill: colors?.success, ...style }} className={className} />,
		CrossCircle: ({ className, style }) => <CrossCircleIcon style={{ fill: colors?.error, ...style }} className={className} />,
		Cross: ({ className, style }) => <Cross style={{ fill: colors?.primary, ...style }} className={className} />,
		Document: ({ className, style }) => <DocumentIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		MoreCircle: ({ className, style }) => <MoreCircleIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		Search: ({ className, style }) => <SearchIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		WarningCircle: ({ className, style }) => <WarningCircleIcon style={{ fill: colors?.warning, ...style }} className={className} />,
		Alert: ({ className, style }) => <AlertIcon style={{ fill: colors?.secondary, ...style }} className={className} />,
		Spinner: ({ className, style }) => <SpinnerIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		Triangle: ({ className, style }) => <TriangleIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		Circle: ({ className, style }) => <CircleIcon style={{ fill: colors?.primary, ...style }} className={className} />,
		CircleChecked: ({ className, style }) => <CircleCheckedIcon style={{ fill: colors?.primary, ...style }} className={className} />,
	}), [colors]);

	return {
		...Icons
	};
}

export function useForm() {
	const [formData, setFormData] = React.useState(FORM_DATA);

	const clearFormData = () => setFormData(FORM_DATA);

	return {
		formData,
		setFormData,
		clearFormData,
	};
}

export function useColors(colorObject) {
	const [colors, setColors] = React.useState(colorObject);

	const changePrimaryColor = primary => {
		setColors({
			...colors,
			text: primary,
			light: pSBC(LIGHT_COLOR_FACTOR, primary, false, true),
			dark: pSBC(-DARK_COLOR_FACTOR, primary, false, true),
		});
	};

	React.useEffect(() => {
		changePrimaryColor(colorObject.primary);
	}, [colorObject]);

	return {
		colors,
		setColors,
		changePrimaryColor
	};
}

export function useLanguageItems({ portal, translate }) {
	/* We save the items once calculated */
	const [languageItems, setLanguageItems] = React.useState();

	/* We get the constante translate items */
	const translateItems = getTranslateItems(translate);

	// Language objects with image and change language option
	const getLanguageItems = React.useCallback(() => {
		const langItems = [];
		const configLanguages = portal?.config?.languages.language ? Object.keys(portal.config.languages.language) : [];
		configLanguages.forEach(lang => {
			langItems.push(translateItems.find(country => country.key === lang));
		});
		return langItems;
	}, [portal?.config?.languages?.language]);

	React.useEffect(() => setLanguageItems(getLanguageItems()), [getLanguageItems]);

	return { languageItems };
}

export function useIsMobileDevice() {
	const [isMobileDevice, setIsMobileDevice] = React.useState(false);

	/* Check if it's desktop or mobile mode */
	React.useEffect(() => setIsMobileDevice(mobileCheck()), []);

	return { isMobileDevice };
}

export function useWindowDimensions() {
	const [width, setWidth] = React.useState(window.innerWidth);
	const [height, setHeight] = React.useState(window.innerHeight);

	const updateWidthAndHeight = () => {
		setWidth(window.innerWidth);
		setHeight(window.innerHeight);
	};

	React.useEffect(() => {
		window.addEventListener('resize', updateWidthAndHeight);
		return () => window.removeEventListener('resize', updateWidthAndHeight);
	});

	return {
		width,
		height,
	};
}

export function useProgressiveImage(src) {
	const [sourceLoaded, setSourceLoaded] = React.useState(null);

	React.useEffect(() => {
		const img = new Image();
		img.src = src;
		img.onload = () => setSourceLoaded(src);
	}, [src]);

	return sourceLoaded;
}

/**
 * Measure DOM node without useRef since it doesnt notice u when updated
 * @returns useState
 */
export function useCallbackRef() {
	const [rect, setRect] = React.useState(null);
	const ref = React.useCallback(node => {
		if (node !== null) {
			setRect(node.getBoundingClientRect());
		}
	}, []);
	return [rect, ref];
}

export const useDownloadDocument = () => {
	const [downloading, setDownloading] = React.useState(false);

	const download = async (path, file) => {
		setDownloading(true);
		const overridePath = path || `${SERVER_URL}/api/councilPortalAttachment/${file.id}`;
		const token = sessionStorage.getItem('token');
		const apiToken = sessionStorage.getItem('apiToken');
		const participantToken = sessionStorage.getItem('participantToken');
		const response = await fetch(overridePath, {
			headers: new Headers({
				'x-jwt-token': token || apiToken || participantToken,
				// 'x-jwt-token': token ? token : apiToken ? apiToken : participantToken,
			})
		});

		if (response.status === 200) {
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = file.filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		}
		setDownloading(false);
	};
	return [downloading, download];
};

export const useRouting = portal => {
	const [routing, setRouting] = React.useState();
	const [lastRoute, setLastRoute] = React.useState();
	const [formData, setFormDataForRouting] = React.useState();
	const [showRoutingPopUp, setShowRoutingPopUp] = React.useState(false);

	/* If home section is active, go home, if not, look for any that is active */
	React.useEffect(() => {
		if (!portal?.config?.sections?.home?.isActive) {
			Object.values(portal?.config?.sections || [])
				?.some(section => {
					if (section.isActive) {
						setRouting(section.value);
					}
					return section.isActive;
				});
			return;
		}
		setRouting('home');
	}, [portal?.config?.sections]);

	const hasChanges = () => JSON.stringify(formData) !== JSON.stringify(FORM_DATA);

	const updateRouting = route => {
		if (['access', 'delegation', 'earlyVote', 'interventions'].includes(routing) && formData && hasChanges(formData)) {
			setShowRoutingPopUp(true);
			setLastRoute(route);
			return;
		}
		if (['home', 'access', 'delegation', 'earlyVote', 'interventions', 'documents'].includes(routing)) {
			setRouting(route);
			return;
		}
		setRouting(null);
	};

	const confirmRoute = () => {
		setRouting(lastRoute);
		setLastRoute();
		setShowRoutingPopUp(false);
	};

	const cancelRoute = () => {
		setLastRoute();
		setShowRoutingPopUp(false);
	};

	return {
		routing, updateRouting, setFormDataForRouting, showRoutingPopUp, setShowRoutingPopUp, confirmRoute, cancelRoute
	};
};

export function useIntelliTags({ portal, section, translate }) {
	const [intelliTags, setIntelliTags] = React.useState();

	const getIntelligTags = React.useCallback(
		() => ({
			companyName: portal?.council?.company?.businessName,
			councilName: portal?.council?.name,
			councilDate: moment(portal?.council?.dateStart)
				?.locale(translate.currentLanguage)
				?.format(portal?.config?.closedPortal?.closeHourFormat),
			councilStreet: portal?.council?.street,
			councilCity: portal?.council?.city,
			councilCloseDay: moment(portal?.config?.portalDates?.close?.general?.date)
				?.locale(translate.currentLanguage)
				?.format(portal?.config?.closedPortal?.closeDayFormat),
			councilCloseHour: moment(portal?.config?.portalDates?.close?.general?.date)
				?.locale(translate.currentLanguage)
				?.format(portal?.config?.closedPortal?.closeHourFormat),
			section: getText(section, translate),
			closeDateSection: moment(portal?.config?.portalDates?.close?.[section?.value]?.date)
				?.locale(translate.currentLanguage)
				?.format(portal?.config?.closedSection?.intelliTagText?.dateFormat),
		}), [portal, section, translate]
	);

	React.useEffect(() => setIntelliTags(getIntelligTags()), [getIntelligTags]);

	const applyIntelliTags = richText => {
		if (!richText) return '';
		let replaced = richText;
		Object.keys(intelliTags || {}).forEach(tag => {
			replaced = replaced.replace(new RegExp(`{{${tag}}}`, 'gi'), intelliTags[tag]);
		});
		return replaced;
	};

	return {
		applyIntelliTags
	};
}
